import { FC, useMemo } from 'react';
import { Checkbox, OverflowMenu, OverflowMenuItem } from '@carbon/react';
import { format0 } from 'utils/number';
import { FloatingTooltip } from 'components/floating-tooltip';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  AddressGroup,
  AddressVariation,
  SubclusterStatus,
} from '../types';
import { PLAIN_TABLE_CONFIG } from '../shared';
import { AddressStatusTag } from './address-tags';
import styles from '../page.module.scss';

// What an execution picks up, and what a reversal can (further) undo.
export const EXECUTABLE_STATUSES: ReadonlySet<SubclusterStatus> = new Set([
  'in_scope',
  'partially_excluded',
]);
export const REVERSIBLE_STATUSES: ReadonlySet<SubclusterStatus> = new Set([
  'combined',
  'partially_reversed',
]);

// The group needs one address to combine into the keeper, so the final included
// discard can't be unticked — exclude the whole group instead.
const LAST_ADDRESS_TOOLTIP =
  "Can't untick the last address in a group. To leave it out, exclude the whole group.";

// A group's expanded variations. Only a standardized group's variations carry
// a checkbox (an identical-text group is one variation the group's own
// checkbox covers); the keeper's is fixed. Once combined, each folded
// variation can be reversed on its own.
export const AddressGroupVariations: FC<{
  group: AddressGroup;
  // No inclusion change: a combine is running, generation is still draining, or
  // generation failed terminally.
  selectionLocked: boolean;
  reverseBlocked: boolean;
  groupPending: boolean;
  pendingIds: Set<number>;
  onToggle: (memberIds: number[], included: boolean) => void;
  onReverse: (variation: AddressVariation) => void;
}> = ({
  group,
  selectionLocked,
  reverseBlocked,
  groupPending,
  pendingIds,
  onToggle,
  onReverse,
}) => {
  const selectable = group.matchedOn === 'standardized';
  const toggleable =
    EXECUTABLE_STATUSES.has(group.status) && !selectionLocked && !groupPending;
  const reversible = REVERSIBLE_STATUSES.has(group.status);
  // Count included discard ROWS, not variations: a discard can share the
  // keeper's text and ride in the keeper variation, so one keeper address is
  // subtracted from the total member rows of the included variations.
  const includedDiscardRows =
    group.variations
      .filter((v) => v.included)
      .reduce((n, v) => n + v.memberIds.length, 0) - 1;
  // A second click sees the stale count before the first lands, so every
  // variation checkbox is held while any one of them has a toggle in flight.
  const anyPending = group.variations.some((v) =>
    v.memberIds.some((m) => pendingIds.has(m))
  );

  const columns = useMemo<DataTableColumnDef<AddressVariation>[]>(() => {
    const select: DataTableColumnDef<AddressVariation> = {
      id: 'included',
      header: '',
      size: 48,
      cell: ({ row }) => {
        const v = row.original;
        const id = `include-variation-${group.id}-${row.index}`;
        // The keeper is fixed; the last still-included discard can't be the one
        // the group has nothing to combine into, so it's held too. A variation
        // can carry several rows (discards that share its text), so it's the last
        // one when it holds every remaining included discard row — not a count of 1.
        const lastIncluded =
          !v.keeper &&
          v.included &&
          toggleable &&
          includedDiscardRows === v.memberIds.length;
        const tooltip = v.keeper
          ? 'Retained address'
          : lastIncluded
          ? LAST_ADDRESS_TOOLTIP
          : null;
        const checkbox = (
          <Checkbox
            id={id}
            labelText={`Include ${v.text}`}
            hideLabel
            checked={v.included}
            disabled={v.keeper || lastIncluded || !toggleable || anyPending}
            aria-describedby={tooltip ? `${id}-tip` : undefined}
            onChange={(_e, { checked }) => onToggle(v.memberIds, checked)}
          />
        );
        return (
          <div className={styles.includeCell}>
            {tooltip ? (
              <FloatingTooltip content={tooltip}>
                {checkbox}
                <span id={`${id}-tip`} className={styles.visuallyHidden}>
                  {tooltip}
                </span>
              </FloatingTooltip>
            ) : (
              checkbox
            )}
          </div>
        );
      },
    };
    const actions: DataTableColumnDef<AddressVariation> = {
      id: 'actions',
      header: '',
      size: 64,
      meta: { overflowVisible: true },
      cell: ({ row }) => {
        const v = row.original;
        if (v.keeper || !v.included || v.reversed) return null;
        return (
          <OverflowMenu
            size="sm"
            flipped
            aria-label={`Actions for ${v.text}`}
            iconDescription={`Actions for ${v.text}`}
          >
            <OverflowMenuItem
              itemText="Reverse address"
              isDelete
              disabled={reverseBlocked}
              onClick={() => onReverse(v)}
            />
          </OverflowMenu>
        );
      },
    };
    return [
      ...(selectable ? [select] : []),
      {
        accessorKey: 'text',
        header: 'Address',
        size: 420,
        meta: { wrap: true, align: 'left' },
        cell: ({ row }) => {
          const v = row.original;
          // After execution a variation says what became of it.
          const outcome = !reversible
            ? null
            : v.reversed
            ? 'reversed'
            : !v.included
            ? 'excluded'
            : null;
          return (
            <span className={styles.variationText}>
              {v.text}
              {outcome && <AddressStatusTag status={outcome} />}
            </span>
          );
        },
      },
      {
        accessorKey: 'docs',
        header: 'Docs',
        size: 120,
        cell: ({ getValue }) => format0(getValue() as number),
      },
      ...(reversible ? [actions] : []),
    ];
  }, [
    group.id,
    selectable,
    toggleable,
    reversible,
    reverseBlocked,
    includedDiscardRows,
    anyPending,
    onToggle,
    onReverse,
  ]);

  return (
    <div className={styles.recordsSubTable}>
      <DataTable<AddressVariation>
        data={group.variations}
        columns={columns}
        getRowId={(v, index) => `${group.id}-${v.memberIds[0] ?? index}`}
        config={{ ...PLAIN_TABLE_CONFIG, hideFooter: true }}
      />
    </div>
  );
};
